// src/pages/AuthCallback.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import posthog from 'posthog-js';
import { PENDING_ATTR_KEY, attributionEventProps } from '../lib/attribution';
import { CheckCircle, XCircle } from 'lucide-react';
import styles from './AuthCallback.module.css';

// Apply attribution stashed before the Google OAuth redirect. Supabase creates
// the account (and fires handle_new_user) before we can attach metadata, so for
// the OAuth path we write the source fields onto the profile here, once, on a
// freshly-created account. Best-effort: never block the auth redirect, and
// degrade quietly if the profiles columns don't exist yet (pre-migration).
async function applyPendingAttribution(user) {
  let pending = null;
  try { pending = JSON.parse(localStorage.getItem(PENDING_ATTR_KEY) || 'null'); } catch { /* ignore */ }
  if (!pending || !user) return;

  const createdRecently = user.created_at &&
    (Date.now() - new Date(user.created_at).getTime() < 5 * 60 * 1000);

  try {
    if (createdRecently) {
      await supabase.from('profiles').update({
        signup_channel: pending.signup_channel || null,
        signup_source: pending.signup_source || null,
        signup_referrer: pending.signup_referrer || null,
        utm_source: pending.utm_source || null,
        utm_medium: pending.utm_medium || null,
        utm_campaign: pending.utm_campaign || null,
        utm_term: pending.utm_term || null,
        utm_content: pending.utm_content || null,
      }).eq('id', user.id);
      supabase.auth.updateUser({ data: pending }).catch(() => {});
      try {
        const role = localStorage.getItem('pendingRole') || undefined;
        posthog.identify(user.id);
        posthog.capture('signup', { method: 'google', role, ...attributionEventProps(pending) });
      } catch { /* analytics best-effort */ }
    }
  } catch { /* pre-migration / RLS — don't block auth */ }
  finally {
    try { localStorage.removeItem(PENDING_ATTR_KEY); } catch { /* ignore */ }
  }
}

// Same-origin path guard for the post-auth redirect target. Mirrors the
// guard in AuthPage.jsx so an attacker can't craft a confirmation link
// that bounces the user to an external host.
function sanitiseNext(raw) {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//')) return null;
  if (!/^\/[a-zA-Z0-9/_\-?=&%.]*$/.test(raw)) return null;
  return raw;
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing authentication...');
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    if (!processed) {
      handleCallback();
    }
  }, [processed]);

  const handleCallback = async () => {
    if (processed) return;
    setProcessed(true);

    try {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const queryParams = new URLSearchParams(window.location.search);

      const hashAccessToken = hashParams.get('access_token');
      const queryCode = queryParams.get('code');
      const queryType = queryParams.get('type');
      const queryToken = queryParams.get('token');
      const next = sanitiseNext(queryParams.get('next')) || '/dashboard';

      // Handle PKCE flow (Supabase v2 default) — code in query params.
      // The Supabase client auto-exchanges the code during its own initialization
      // (detectSessionInUrl: true by default), so we must NOT call
      // exchangeCodeForSession() again — the code verifier is already consumed.
      // Instead, just wait for the session to be available.
      if (queryCode) {
        setMessage('Processing Google sign-in...');
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (!session) throw new Error('No session created from code exchange');

        await applyPendingAttribution(session.user);

        setStatus('success');
        setMessage('Authentication successful! Redirecting...');
        setTimeout(() => navigate(next), 1500);
        return;
      }

      if (queryToken || queryType === 'signup' || queryType === 'email' || queryType === 'magiclink') {
        setMessage('Confirming your email...');

        if (queryToken) {
          const { error } = await supabase.auth.verifyOtp({ token_hash: queryToken, type: 'email' });
          if (error) throw new Error('Failed to verify email token');
        }

        await new Promise(resolve => setTimeout(resolve, 1500));
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (session) {
          setStatus('success');
          setMessage('Email confirmed! Redirecting...');
          setTimeout(() => navigate(next), 1500);
        } else {
          setStatus('success');
          setMessage('Email confirmed! Please sign in.');
          setTimeout(() => navigate(`/auth?redirect=${encodeURIComponent(next)}`), 2000);
        }
        return;
      }

      if (hashAccessToken) {
        setMessage('Processing Google sign-in...');
        const refreshToken = hashParams.get('refresh_token');
        const { data, error } = await supabase.auth.setSession({
          access_token: hashAccessToken,
          refresh_token: refreshToken,
        });
        if (error) throw error;
        if (!data?.session) throw new Error('No session created from tokens');

        setStatus('success');
        setMessage('Authentication successful! Redirecting...');
        window.history.replaceState(null, '', window.location.pathname);
        setTimeout(() => navigate(next), 1500);
        return;
      }

      const { data: { session }, error } = await supabase.auth.getSession();
      if (session) {
        setStatus('success');
        setMessage('Already authenticated! Redirecting...');
        setTimeout(() => navigate(next), 1000);
      } else {
        throw new Error('No authentication data found');
      }
    } catch (error) {
      console.error('Auth callback error:', error);
      setStatus('error');
      setMessage(error.message || 'Authentication failed');
      setTimeout(() => navigate('/auth'), 3000);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.iconWrap}>
          {status === 'processing' && <div className={styles.spinner} />}
          {status === 'success'    && <CheckCircle className={styles.successIcon} size={64} />}
          {status === 'error'      && <XCircle     className={styles.errorIcon}   size={64} />}
        </div>

        <h2 className={styles.title}>
          {status === 'processing' && 'Authenticating...'}
          {status === 'success'    && 'Success!'}
          {status === 'error'      && 'Oops!'}
        </h2>

        <p className={styles.message}>{message}</p>
      </div>
    </div>
  );
}
